import { Injectable } from '@angular/core';
import { Observable, forkJoin, throwError } from 'rxjs';
import { Order } from '../../shared/models/Order';
import { OrderService } from './order.service';

export type PackingPerPage = 1 | 2 | 3;
export type PackingPrintFilter = '' | 'SIN' | 'IMPRESA';

export const PACKING_PER_PAGE_OPTIONS: { label: string; value: PackingPerPage }[] = [
  { label: '1 por hoja', value: 1 },
  { label: '2 por hoja', value: 2 },
  { label: '3 por hoja', value: 3 }
];

export const PACKING_STATUS_OPTIONS: { label: string; value: PackingPrintFilter }[] = [
  { label: 'Todas', value: '' },
  { label: 'Sin imprimir', value: 'SIN' },
  { label: 'Ya impresas', value: 'IMPRESA' }
];

export function guiaImpresaQuery(filter: PackingPrintFilter): boolean | undefined {
  if (filter === 'SIN') return false;
  if (filter === 'IMPRESA') return true;
  return undefined;
}

@Injectable({ providedIn: 'root' })
export class PackingPrintService {

  constructor(private orderService: OrderService) {}

  printAndMark(orders: Order[], perPage: PackingPerPage): Observable<Order[]> {
    if (!orders?.length) {
      return throwError(() => ({ code: 'EMPTY' }));
    }
    return new Observable(subscriber => {
      const opened = this.openAndPrint(orders, perPage, () => {
        forkJoin(orders.map(o => this.orderService.marcarGuiaImpresa(o.id, true))).subscribe({
          next: updated => {
            subscriber.next(updated);
            subscriber.complete();
          },
          error: err => subscriber.error(err)
        });
      });
      if (!opened) {
        subscriber.error({ code: 'POPUP' });
      }
    });
  }

  private openAndPrint(orders: Order[], perPage: PackingPerPage, afterPrint: () => void): boolean {
    const safePerPage = ([1, 2, 3].includes(perPage) ? perPage : 1) as PackingPerPage;
    const html = this.buildDocument(orders, safePerPage);
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return false;
    try { win.opener = null; } catch { /* ignore */ }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      try {
        win.print();
      } finally {
        try { win.close(); } catch { /* ignore */ }
        afterPrint();
      }
    }, 350);
    return true;
  }

  private buildDocument(orders: Order[], perPage: PackingPerPage): string {
    const pages: string[] = [];
    for (let i = 0; i < orders.length; i += perPage) {
      const chunk = orders.slice(i, i + perPage);
      const slips = chunk.map(o => this.buildSlip(o)).join('');
      pages.push(`<section class="page per-${perPage}">${slips}</section>`);
    }

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Hojas de empaque</title>
  <style>${this.styles()}</style>
</head>
<body>
  ${pages.join('')}
</body>
</html>`;
  }

  private buildSlip(order: Order): string {
    const tipo = this.esc(order.tipoEnvio?.nombreTipoEnvio || order.deliveryMethod || '—');
    const distrito = this.esc(order.distrito?.nombre || order.district || '');
    const address = this.esc(order.deliveryAddress || '');
    const ref = this.esc(order.reference || '');
    const notes = this.esc(order.notes || '');
    const dni = this.esc(String(order.dni ?? '').trim());
    const date = this.formatDate(order.scheduledDate);
    const items = (order.items || []).map(item => {
      const name = this.esc(
        item?.product?.productName || item?.product?.name || item?.productName || 'Producto'
      );
      const size = this.esc(item?.size || '—');
      const color = this.esc(item?.color || '—');
      const qty = Number(item?.quantity) || 1;
      return `<tr>
        <td>${name}</td>
        <td>${color}</td>
        <td>${size}</td>
        <td class="qty">${qty}</td>
      </tr>`;
    }).join('') || `<tr><td colspan="4">Sin productos</td></tr>`;

    const entregaRow = this.isDelivery(order)
      ? `<div><span>Entrega</span><strong>${date}</strong></div>`
      : '';

    return `<article class="slip">
      <header>
        <div class="brand">MOA NANI</div>
        <div class="oid">Pedido #${order.id}</div>
      </header>
      <div class="meta">
        <div><span>Cliente</span><strong>${this.esc(order.clientName || '—')}</strong></div>
        <div><span>Teléfono</span><strong>${this.esc(order.clientPhone || '—')}</strong></div>
        <div><span>DNI / CE</span><strong>${dni || '&nbsp;'}</strong></div>
        <div><span>Envío</span><strong>${tipo}</strong></div>
        ${entregaRow}
      </div>
      <div class="addr">
        <span>Dirección</span>
        <strong>${[address, distrito].filter(Boolean).join(', ') || '—'}</strong>
        ${ref ? `<em>Ref: ${ref}</em>` : ''}
      </div>
      <table>
        <thead>
          <tr><th>Producto</th><th>Color</th><th>Talla</th><th>Cant.</th></tr>
        </thead>
        <tbody>${items}</tbody>
      </table>
      ${notes ? `<div class="notes"><span>Nota</span><p>${notes}</p></div>` : ''}
    </article>`;
  }

  private isDelivery(order: Order): boolean {
    const method = String(order.deliveryMethod || '').toUpperCase();
    if (method === 'DELIVERY') return true;
    const nombre = String(order.tipoEnvio?.nombreTipoEnvio || '').toLowerCase();
    return nombre.includes('delivery');
  }

  private formatDate(value?: string): string {
    if (!value) return '—';
    const part = String(value).split('T')[0];
    const [y, m, d] = part.split('-');
    if (!y || !m || !d) return this.esc(part);
    return `${d}/${m}/${y}`;
  }

  private esc(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private styles(): string {
    return `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Helvetica, Arial, sans-serif; color: #111; background: #fff; }
      @page { size: A4; margin: 6mm; }
      .page {
        width: 100%;
        min-height: 285mm;
        display: flex;
        flex-direction: column;
        gap: 4mm;
        page-break-after: always;
      }
      .page:last-child { page-break-after: auto; }
      .slip {
        flex: 1;
        border: 2px solid #111;
        border-radius: 8px;
        padding: 12px 14px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .per-1 .slip { min-height: 275mm; }
      .per-2 .slip { min-height: 134mm; }
      .per-3 .slip { min-height: 88mm; }
      .per-2 .slip + .slip,
      .per-3 .slip + .slip {
        border-top: 1px dashed #888;
      }
      header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        border-bottom: 2px solid #111;
        padding-bottom: 8px;
        margin-bottom: 10px;
      }
      .brand { font-weight: 800; letter-spacing: 0.12em; font-size: 18px; }
      .oid { font-weight: 800; font-size: 20px; }
      .per-1 .brand { font-size: 26px; }
      .per-1 .oid { font-size: 30px; }
      .per-2 .brand { font-size: 22px; }
      .per-2 .oid { font-size: 24px; }
      .meta {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px 14px;
        margin-bottom: 10px;
        font-size: 14px;
      }
      .per-1 .meta { font-size: 18px; gap: 12px 18px; }
      .meta span, .addr span, .notes span {
        display: block;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #666;
        font-weight: 700;
      }
      .per-1 .meta span, .per-1 .addr span, .per-1 .notes span { font-size: 12px; }
      .addr { margin-bottom: 10px; font-size: 14px; }
      .per-1 .addr { font-size: 18px; }
      .addr em { display: block; font-style: italic; color: #444; margin-top: 3px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      .per-1 table { font-size: 16px; }
      th, td { border-bottom: 1px solid #ddd; padding: 5px 7px; text-align: left; }
      th { background: #111; color: #fff; font-size: 11px; text-transform: uppercase; }
      .per-1 th { font-size: 13px; padding: 8px 10px; }
      .qty { text-align: center; font-weight: 700; width: 52px; }
      .notes { margin-top: 8px; font-size: 13px; }
      .per-1 .notes { font-size: 16px; }
      .notes p { margin-top: 2px; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `;
  }
}
